import React, { useState, useCallback, memo } from 'react';
import firebase from 'firebase';
import { MdOutlineMailOutline } from "react-icons/md";
import * as Sentry from '@sentry/nextjs';
import { useApiGateway } from 'hooks/useApi';
import classNames from 'styles/utils/classNames';

import { auth } from 'services/client/firebase';
import Google from 'svg/Google';

const NEWSLETTER_KEY = 'AlertsNewsletter';

interface AuthFormProps {
  onSubmitSuccess?: () => void;
}

const AuthForm = memo(({ onSubmitSuccess }: AuthFormProps) => {
  const [email, setEmail] = useState('');
  const { post: newsletterSignupFetch, isLoading, isSuccess } = useApiGateway('/v1/email-list');
  const isDisabled = isLoading;

  const handleNewsletterSignup = useCallback(async (emailToUse: string) => {
    try {
      await newsletterSignupFetch({
        payload: {
          email: emailToUse,
          listId: NEWSLETTER_KEY,
        },
      });

      onSubmitSuccess?.();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }, [newsletterSignupFetch, onSubmitSuccess]);

  const handleGoogleSignup = useCallback(async () => {

    if (isDisabled) return;

    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await auth.signInWithPopup(provider);
      if (result.user?.email) {
        await handleNewsletterSignup(result.user.email);
        setEmail('');
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [isDisabled, handleNewsletterSignup]);

  const handleEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDisabled) return;

    try {
      await handleNewsletterSignup(email);
      setEmail('');
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [email, isDisabled, handleNewsletterSignup]);

  return (
    <>
      {isSuccess ? (
        <div className="text-pretty rounded-2xl bg-white p-4 text-center font-bold text-green-600">
          Success! You'll receive our free daily newsletter.
        </div>
      ) : (
        <div className="space-y-4">
          <form
            onSubmit={handleEmailSubmit}
            className="space-y-3"
          >
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <div className="relative">
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={classNames(
                    'w-full rounded border border-gray-600 bg-gray-800/50 px-3 py-2 text-white placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-brand-primary focus:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-primary/20',
                    isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:border-gray-500'
                  )}
                  placeholder="Email..."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isDisabled}
                />
              </div>
            </div>
            <button
              type="submit"
              className={classNames(
                'w-full rounded border border-brand-primary bg-brand-primary px-3 py-2 text-center text-sm font-bold transition-colors hover:border-white hover:bg-white hover:text-brand-primary',
                isDisabled ? 'cursor-not-allowed opacity-50' : ''
              )}
              disabled={isDisabled}
            >
              {isDisabled ? (
                <div className="flex items-center justify-center space-x-2">
                  <svg className="h-3 w-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Submitting...</span>
                </div>
              ) : (
                <div className='flex items-center  justify-center space-x-3 gap-2'><MdOutlineMailOutline size={20} /> Subscribe with Email</div>
              )}
            </button>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-palette-black-3 px-2 text-gray-500">Or subscribe with</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={isDisabled}
            className={classNames(
              'flex w-full items-center justify-center space-x-2 rounded border border-gray-600 bg-palette-black-3 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-palette-black-4 disabled:opacity-50',
              isDisabled ? 'cursor-not-allowed opacity-50' : ''
            )}
          >
            <Google className="h-5 w-5" />
            <span>{isDisabled ? 'Subscribing...' : 'Subscribe with Google'}</span>
          </button>
        </div>
      )}
    </>
  );
});

AuthForm.displayName = 'AuthForm';

export default AuthForm;